import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Settings,
  Plus,
  User,
  FolderOpen,
  RefreshCw,
  Clock,
  CheckCircle,
  AlertCircle,
  Pause,
  XCircle,
  Package,
  Calendar,
  DollarSign,
  Play,
  Mail,
  FileText,
  Download,
  Eye,
  File,
  ChevronRight,
  Filter,
  Search
} from 'lucide-react';
import { useAuth } from '../AuthProvider';
import { projectManagementApiService, ProjectManagement } from '../../services/projectManagementApi';
import { serviceApiService, Service } from '../../services/serviceApi';
import api from '../../services/api';
import { packsApiService, Pack } from '../../services/packsApi';

interface SubmittedProject {
  id: number;
  title: string;
  description: string;
  message: string;
  files: any[];
  files_count: number;
  status: string;
  status_display: string;
  status_color: string;
  user_name: string;
  user_email: string;
  admin_response: string;
  created_at: string;
}

interface MobileGestionProjetsProps {
  onClose: () => void;
}

const MobileGestionProjets: React.FC<MobileGestionProjetsProps> = ({ onClose }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'projects' | 'submitted'>('projects');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedProject, setSelectedProject] = useState<ProjectManagement | null>(null);
  const [selectedSubmittedProject, setSelectedSubmittedProject] = useState<SubmittedProject | null>(null);
  const [showProjectDetail, setShowProjectDetail] = useState(false);
  const [showCreateStep, setShowCreateStep] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(null);
  const [newStepName, setNewStepName] = useState('');
  const [newStepDescription, setNewStepDescription] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStep, setSelectedStep] = useState<any>(null);
  const [newStatus, setNewStatus] = useState<'pending' | 'in_progress' | 'completed'>('pending');
  const [availableServices, setAvailableServices] = useState<Service[]>([]);
  const [availablePacks, setAvailablePacks] = useState<Pack[]>([]);
  const [projectForm, setProjectForm] = useState({
    project_name: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    client_company: '',
    description: '',
    services: [] as any[]
  });
  
  // Projects data
  const [projects, setProjects] = useState<ProjectManagement[]>([]);
  const [submittedProjects, setSubmittedProjects] = useState<SubmittedProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Load projects data
  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (activeTab === 'projects') {
        const projectsData = await projectManagementApiService.getProjectsManagement();
        setProjects(projectsData);
      } else {
        // Load submitted projects
        const response = await api.get('/api/submitted-projects/all/');
        const submittedData = response.data;
        
        if (submittedData) {
          console.log('📊 Données projets personnels reçues:', submittedData);
          
          // Vérifier si c'est un tableau ou un objet avec results/projects
          if (Array.isArray(submittedData)) {
            setSubmittedProjects(submittedData);
          } else if (submittedData.results && Array.isArray(submittedData.results)) {
            setSubmittedProjects(submittedData.results);
          } else if (submittedData.projects && Array.isArray(submittedData.projects)) {
            setSubmittedProjects(submittedData.projects);
          } else {
            console.warn('⚠️ Format de données inattendu pour les projets personnels:', submittedData);
            setSubmittedProjects([]);
          }
        }
      }
    } catch (err) {
      console.error('Erreur lors du chargement des projets:', err);
      setError('Erreur lors du chargement des projets');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadProjects();
    loadAvailableServicesAndPacks();
  }, [loadProjects]);

  const loadAvailableServicesAndPacks = async () => {
    try {
      console.log('🔄 Chargement des services et packs depuis le backend...');
      
      // Charger les services et packs en parallèle
      const [servicesData, packsData] = await Promise.all([
        serviceApiService.getServices(true), // Seulement les services actifs
        packsApiService.getPacks(true) // Seulement les packs actifs
      ]);

      console.log('✅ Services chargés:', servicesData.length);
      console.log('✅ Packs chargés:', packsData.length);

      setAvailableServices(servicesData);
      setAvailablePacks(packsData);
    } catch (error) {
      console.error('❌ Erreur lors du chargement des services/packs:', error);
      // En cas d'erreur, laisser les tableaux vides
      setAvailableServices([]);
      setAvailablePacks([]);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadProjects();
    setRefreshing(false);
  };

  const handleProjectClick = (project: ProjectManagement) => {
    setSelectedProject(project);
    setSelectedSubmittedProject(null);
    setShowProjectDetail(true);
  };

  const handleSubmittedProjectClick = (project: SubmittedProject) => {
    setSelectedSubmittedProject(project);
    setSelectedProject(null);
    setShowProjectDetail(true);
  };

  const handleCreateStep = async () => {
    if (!selectedServiceId || !newStepName.trim()) return;

    try {
      console.log('📤 Création étape pour service ID:', selectedServiceId, {
        name: newStepName,
        description: newStepDescription
      });
      
      await projectManagementApiService.createConceptionStep(selectedServiceId, {
        name: newStepName,
        description: newStepDescription
      });

      // Recharger les données du projet - FORCE le rechargement
      setLoading(true);
      await loadProjects();
      setNewStepName('');
      setNewStepDescription('');
      setSelectedServiceId(null);
      setShowCreateStep(false);
      
      alert('Étape créée avec succès !');
    } catch (error) {
      console.error('Erreur lors de la création de l\'étape:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      alert('Erreur lors de la création de l\'étape: ' + errorMessage);
    }
  };

  const handleAddStepToService = (serviceId: number) => {
    setSelectedServiceId(serviceId);
    setShowCreateStep(true);
  };

  const handleChangeStepStatus = (step: any) => {
    setSelectedStep(step);
    setNewStatus(step.status);
    setShowStatusModal(true);
  };

  const handleUpdateStepStatus = async () => {
    if (!selectedStep) return;

    try {
      console.log('📤 Mise à jour statut étape ID:', selectedStep.id, 'nouveau statut:', newStatus);
      
      await projectManagementApiService.updateConceptionStep(selectedStep.id, {
        status: newStatus
      });

      // Recharger les données du projet - FORCE le rechargement
      setLoading(true);
      await loadProjects();
      setShowStatusModal(false);
      setSelectedStep(null);
      
      alert('Statut mis à jour avec succès !');
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      alert('Erreur lors de la mise à jour du statut: ' + errorMessage);
    }
  };

  // Fonctions helper pour gérer les URLs de fichiers selon leur type
  const getFileUrl = (file: any): string => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://web-production-83f53.up.railway.app';
    
    if (file.type === 'pdf' && file.pdf_id) {
      return `${API_BASE_URL}/api/pdf/${file.pdf_id}/`;
    } else {
      return file.url || file.file_url || file.file || '';
    }
  };

  const getDownloadUrl = (file: any): string => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://web-production-83f53.up.railway.app';
    
    if (file.type === 'pdf' && file.pdf_id) {
      return `${API_BASE_URL}/api/pdf/${file.pdf_id}/download/`;
    } else {
      return file.url || file.file_url || file.file || '';
    }
  };

  const handleViewFile = (file: any, fileName: string) => {
    const fileUrl = getFileUrl(file);
    console.log('📂 Opening file:', fileName, 'Type:', file.type, 'URL:', fileUrl);
    if (!fileUrl) {
      alert('URL du fichier non disponible');
      return;
    }
    window.open(fileUrl, '_blank');
  };

  const handleDownloadFile = (file: any, fileName: string) => {
    const fileUrl = getDownloadUrl(file);
    console.log('⬇️ Downloading file:', fileName, 'Type:', file.type, 'URL:', fileUrl);
    if (!fileUrl) {
      alert('URL du fichier non disponible');
      return;
    }
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName || 'fichier';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleStatusChange = async (projectId: number, newStatus: string) => {
    try {
      const response = await api.put(`/api/submitted-projects/${projectId}/update/`, {
        status: newStatus
      });

      const data = response.data;
      
      if (data.success) {
        // Mettre à jour le projet dans la liste
        setSubmittedProjects(prev => prev.map(p => 
          p.id === projectId ? data.project : p
        ));
        alert('Statut mis à jour avec succès !');
      } else {
        alert(data.error || 'Erreur lors de la mise à jour du statut');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    try {
      if (!dateString || dateString === '') {
        return 'Date non disponible';
      }
      const cleanedDate = String(dateString).trim();
      const date = new Date(cleanedDate);
      if (isNaN(date.getTime())) {
        return 'Date non disponible';
      }
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Date non disponible';
    }
  };

  const handleCreateProject = async () => {
    if (!projectForm.project_name.trim() || !projectForm.client_name.trim() || !projectForm.client_email.trim()) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const projectData = {
        project_name: projectForm.project_name,
        client_data: {
          name: projectForm.client_name,
          email: projectForm.client_email,
          phone: projectForm.client_phone || '',
          company: projectForm.client_company || '',
          address: ''
        },
        services: [], // Commencer avec un tableau vide, les services seront ajoutés après
        description: projectForm.description || ''
      };

      console.log('📤 Données du projet à créer:', projectData);
      await projectManagementApiService.createProjectManually(projectData);

      // Recharger les données - FORCE le rechargement
      setLoading(true);
      await loadProjects();
      
      // Reset form
      setProjectForm({
        project_name: '',
        client_name: '',
        client_email: '',
        client_phone: '',
        client_company: '',
        description: '',
        services: []
      });
      setShowCreateModal(false);
      
      alert('Projet créé avec succès !');
    } catch (error) {
      console.error('Erreur lors de la création du projet:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      alert('Erreur lors de la création du projet: ' + errorMessage);
    }
  };

  // const getStatusLabel = (status: string) => {
  //   switch (status) {
  //     case 'pending':
  //       return 'En attente';
  //     case 'in_progress':
  //       return 'En cours';
  //     case 'completed':
  //       return 'Terminé';
  //     default:
  //       return status;
  //   }
  // };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'in_progress':
        return <Play className="w-4 h-4 text-blue-500" />;
      case 'paused':
        return <Pause className="w-4 h-4 text-orange-500" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'paused':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'completed':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'cancelled':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };


  const filteredProjects = projects.filter(project => {
    const matchesSearch = 
      project.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.client_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.project_id?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const filteredSubmittedProjects = Array.isArray(submittedProjects) ? submittedProjects.filter(project => {
    const matchesSearch = 
      project.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.user_email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;

    return matchesSearch && matchesStatus;
  }) : [];

  if (!user?.isAdmin) {
    return (
      <div className="fixed inset-0 bg-gray-50 z-50 flex items-center justify-center">
        <div className="text-center p-6">
          <Settings className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Accès non autorisé</h2>
          <p className="text-gray-600 mb-4">Vous devez être administrateur pour accéder à cette page.</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-50 z-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-amber-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Chargement des projets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-50 z-50 overflow-hidden">
      {/* Header Mobile */}
      <div className="bg-gradient-to-r from-amber-600 to-amber-700 shadow-lg">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-amber-100 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Retour</span>
          </button>

          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
              <Settings className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Projets</h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {activeTab === 'projects' && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="p-2 text-amber-100 hover:text-white transition-colors"
              >
                <Plus className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 text-amber-100 hover:text-white transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-4 pb-4">
          <div className="flex bg-amber-500/20 backdrop-blur-sm rounded-xl p-1">
            <button
              onClick={() => setActiveTab('projects')}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'projects'
                  ? 'bg-white text-amber-700 shadow-sm'
                  : 'text-amber-100 hover:text-white'
              }`}
            >
              <Settings className="w-4 h-4" />
              <span>Services/Packs</span>
            </button>
            <button
              onClick={() => setActiveTab('submitted')}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'submitted'
                  ? 'bg-white text-amber-700 shadow-sm'
                  : 'text-amber-100 hover:text-white'
              }`}
            >
              <User className="w-4 h-4" />
              <span>Personnels</span>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto" style={{ height: 'calc(100vh - 140px)' }}>
        {/* Search and Filters */}
        <div className="p-4">
          <div className="flex gap-2 mb-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher un projet..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-3 rounded-xl border transition-colors ${
                showFilters 
                  ? 'bg-amber-500 text-white border-amber-500' 
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Filter className="w-4 h-4" />
            </button>
          </div>

          {/* Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="bg-white rounded-xl border border-gray-200 p-3 mb-3"
              >
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'all', label: 'Tous', icon: FolderOpen },
                    { key: 'pending', label: 'En attente', icon: Clock },
                    { key: 'in_progress', label: 'En cours', icon: Play },
                    { key: 'completed', label: 'Terminés', icon: CheckCircle }
                  ].map(({ key, label, icon: Icon }) => (
                    <button
                      key={key}
                      onClick={() => setStatusFilter(key)}
                      className={`flex items-center gap-2 p-2 rounded-lg text-sm transition-colors ${
                        statusFilter === key
                          ? 'bg-amber-100 text-amber-700 border border-amber-200'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Projects List */}
        <div className="px-4 pb-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {activeTab === 'projects' ? (
              // Service Projects
              filteredProjects.length === 0 ? (
                <div className="text-center py-12">
                  <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun projet trouvé</h3>
                  <p className="text-gray-500">Aucun projet ne correspond à vos critères.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredProjects.map((project, index) => (
                    <motion.div
                      key={project.project_id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => handleProjectClick(project)}
                      className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <Package className="w-4 h-4 text-amber-600 flex-shrink-0" />
                            <h3 className="font-medium text-gray-900 truncate">
                              Projet #{project.project_id}
                            </h3>
                          </div>
                          
                          <div className="space-y-1 mb-3">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <User className="w-3 h-3" />
                              <span className="truncate">{project.client_name}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Mail className="w-3 h-3" />
                              <span className="truncate">{project.client_email}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Calendar className="w-3 h-3" />
                              <span>{formatDate(project.created_at)}</span>
                            </div>
                            {project.services && project.services.length > 0 && (
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Package className="w-3 h-3" />
                                <span>{project.services.length} service(s)</span>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center justify-between">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${getStatusColor(project.status)}`}>
                              {getStatusIcon(project.status)}
                              {project.status}
                            </span>
                            
                            {project.total_amount && (
                              <div className="flex items-center gap-1 text-sm font-medium text-gray-900">
                                <DollarSign className="w-3 h-3" />
                                {project.total_amount} FCFA
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <ChevronRight className="w-4 h-4 text-gray-400 ml-2 flex-shrink-0" />
                      </div>
                    </motion.div>
                  ))}
                </div>
              )
            ) : (
              // Submitted Projects
              filteredSubmittedProjects.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun projet personnel</h3>
                  <p className="text-gray-500">Aucun projet personnel soumis.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredSubmittedProjects.map((project, index) => (
                    <motion.div
                      key={project.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => handleSubmittedProjectClick(project)}
                      className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                            <h3 className="font-medium text-gray-900 truncate">
                              {project.title}
                            </h3>
                          </div>
                          
                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                            {project.description}
                          </p>
                          
                          {/* Message additionnel */}
                          {project.message && (
                            <div className="p-2 bg-blue-50 rounded-lg border-l-2 border-blue-400 mb-3">
                              <p className="text-xs font-medium text-blue-900 mb-1">💬 Message</p>
                              <p className="text-xs text-blue-800">{project.message}</p>
                            </div>
                          )}

                          {/* Fichiers joints */}
                          {project.files && project.files.length > 0 && (
                            <div className="mb-3">
                              <p className="text-xs font-medium text-gray-700 mb-2 flex items-center gap-1">
                                <FileText className="w-3 h-3 text-purple-600" />
                                Fichiers joints ({project.files.length})
                              </p>
                              <div className="grid grid-cols-4 gap-1">
                                {project.files.slice(0, 8).map((file: any, index: number) => {
                                  const fileName = file.original_name || file.file_name || file.name || `Fichier ${index + 1}`;
                                  const fileUrl = getFileUrl(file);
                                  const isImage = fileName.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|svg|bmp)$/);
                                  const isPDF = file.type === 'pdf' || fileName.toLowerCase().endsWith('.pdf');

                                  return (
                                    <div 
                                      key={index}
                                      onClick={() => handleViewFile(file, fileName)}
                                      className="group relative bg-purple-50 rounded border border-purple-200 hover:border-purple-400 cursor-pointer transition-all duration-200 aspect-square overflow-hidden"
                                    >
                                      <div className="absolute inset-0 flex items-center justify-center p-1">
                                        {isImage ? (
                                          <img 
                                            src={fileUrl} 
                                            alt={fileName}
                                            className="w-full h-full object-cover rounded"
                                            onError={(e) => {
                                              e.currentTarget.style.display = 'none';
                                              e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                            }}
                                          />
                                        ) : null}
                                        <File className={`w-4 h-4 text-purple-400 ${isImage ? 'hidden' : ''}`} />
                                      </div>
                                      
                                      {/* Overlay au hover */}
                                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center">
                                        <Eye className="w-3 h-3 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                                      </div>
                                      
                                      {/* Bouton téléchargement */}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDownloadFile(fileUrl, fileName);
                                        }}
                                        className="absolute top-0.5 right-0.5 p-0.5 bg-white/90 hover:bg-white rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                        title="Télécharger"
                                      >
                                        <Download className="w-2 h-2 text-purple-600" />
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                              {project.files.length > 8 && (
                                <p className="text-xs text-gray-500 text-center mt-1">+ {project.files.length - 8} autre(s)</p>
                              )}
                            </div>
                          )}

                          {/* Réponse admin */}
                          {project.admin_response && (
                            <div className="p-2 bg-green-50 rounded-lg border-l-2 border-green-400 mb-3">
                              <p className="text-xs font-medium text-green-900 mb-1">✅ Réponse de l'équipe</p>
                              <p className="text-xs text-green-800">{project.admin_response}</p>
                            </div>
                          )}

                          <div className="space-y-1 mb-3">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <User className="w-3 h-3" />
                              <span className="truncate">{project.user_name}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Mail className="w-3 h-3" />
                              <span className="truncate">{project.user_email}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Calendar className="w-3 h-3" />
                              <span>{formatDate(project.created_at)}</span>
                            </div>
                          </div>

                          {/* Statut - modifiable pour admin */}
                          {user?.isAdmin ? (
                            <select
                              value={project.status}
                              onChange={(e) => handleStatusChange(project.id, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border cursor-pointer ${getStatusColor(project.status)}`}
                            >
                              <option value="pending">⏳ En attente</option>
                              <option value="reviewing">👁️ En examen</option>
                              <option value="accepted">✅ Accepté</option>
                              <option value="rejected">❌ Rejeté</option>
                              <option value="in_progress">🔨 En cours</option>
                              <option value="completed">✔️ Terminé</option>
                            </select>
                          ) : (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${getStatusColor(project.status)}`}>
                              {getStatusIcon(project.status)}
                              {project.status_display}
                            </span>
                          )}
                        </div>
                        
                        <ChevronRight className="w-4 h-4 text-gray-400 ml-2 flex-shrink-0" />
                      </div>
                    </motion.div>
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      </div>

      {/* Create Project Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-60"
              onClick={() => setShowCreateModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: '100%' }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: '100%' }}
              className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl z-70 max-h-[90vh] overflow-y-auto"
            >
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Créer un Nouveau Projet</h3>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <XCircle className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>

              <div className="p-4 space-y-4">
                {/* Project Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom du projet *
                  </label>
                  <input
                    type="text"
                    value={projectForm.project_name}
                    onChange={(e) => setProjectForm({...projectForm, project_name: e.target.value})}
                    placeholder="Ex: Site web pour entreprise ABC"
                    className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>

                {/* Client Info */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Informations client</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom du client *
                      </label>
                      <input
                        type="text"
                        value={projectForm.client_name}
                        onChange={(e) => setProjectForm({...projectForm, client_name: e.target.value})}
                        placeholder="Nom complet du client"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        value={projectForm.client_email}
                        onChange={(e) => setProjectForm({...projectForm, client_email: e.target.value})}
                        placeholder="email@exemple.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        value={projectForm.client_phone}
                        onChange={(e) => setProjectForm({...projectForm, client_phone: e.target.value})}
                        placeholder="771742350"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Entreprise
                      </label>
                      <input
                        type="text"
                        value={projectForm.client_company}
                        onChange={(e) => setProjectForm({...projectForm, client_company: e.target.value})}
                        placeholder="Nom de l'entreprise"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description du projet
                  </label>
                  <textarea
                    value={projectForm.description}
                    onChange={(e) => setProjectForm({...projectForm, description: e.target.value})}
                    placeholder="Description détaillée du projet..."
                    rows={3}
                    className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                  />
                </div>

                {/* Services et Packs */}
                <div className="space-y-4">
                  {/* Services */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Services ({availableServices.length} disponibles)
                    </label>
                    <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-xl p-3">
                      {availableServices.map((service) => {
                        const isSelected = projectForm.services.some(s => s.service_id === service.id);
                        return (
                          <label key={service.id} className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  // Ajouter le service
                                  const newService = {
                                    type: 'service' as const,
                                    service_id: service.id,
                                    quantity: 1,
                                    unit_price: service.price,
                                    description: service.name
                                  };
                                  setProjectForm(prev => ({
                                    ...prev,
                                    services: [...prev.services, newService]
                                  }));
                                } else {
                                  // Supprimer le service
                                  setProjectForm(prev => ({
                                    ...prev,
                                    services: prev.services.filter(s => s.service_id !== service.id)
                                  }));
                                }
                              }}
                              className="w-4 h-4 text-amber-600 bg-gray-100 border-gray-300 rounded focus:ring-amber-500"
                            />
                            <div className="flex-1 flex justify-between items-center">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{service.name}</p>
                                <p className="text-xs text-gray-500">{service.price_display}</p>
                              </div>
                              <span className="text-sm font-medium text-amber-600">{service.price} FCFA</span>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Packs */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Packs ({availablePacks.length} disponibles)
                    </label>
                    <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-xl p-3">
                      {availablePacks.map((pack) => {
                        const isSelected = projectForm.services.some(s => s.pack_id === pack.id);
                        return (
                          <label key={pack.id} className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  // Ajouter le pack
                                  const newPack = {
                                    type: 'pack' as const,
                                    pack_id: pack.id,
                                    quantity: 1,
                                    unit_price: pack.total_price,
                                    description: pack.name
                                  };
                                  setProjectForm(prev => ({
                                    ...prev,
                                    services: [...prev.services, newPack]
                                  }));
                                } else {
                                  // Supprimer le pack
                                  setProjectForm(prev => ({
                                    ...prev,
                                    services: prev.services.filter(s => s.pack_id !== pack.id)
                                  }));
                                }
                              }}
                              className="w-4 h-4 text-amber-600 bg-gray-100 border-gray-300 rounded focus:ring-amber-500"
                            />
                            <div className="flex-1 flex justify-between items-center">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{pack.name}</p>
                                <p className="text-xs text-gray-500">{pack.services.length} service(s) inclus</p>
                                {pack.payment_facility && (
                                  <p className="text-xs text-green-600">{pack.payment_facility}</p>
                                )}
                              </div>
                              <span className="text-sm font-medium text-amber-600">{pack.total_price} FCFA</span>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Résumé des sélections */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="text-sm font-medium text-amber-800">
                        Sélection ({projectForm.services.length} élément{projectForm.services.length > 1 ? 's' : ''})
                      </h5>
                      {projectForm.services.length > 0 && (
                        <button
                          onClick={() => setProjectForm(prev => ({ ...prev, services: [] }))}
                          className="text-xs text-red-600 hover:text-red-800"
                        >
                          Tout supprimer
                        </button>
                      )}
                    </div>
                    
                    {projectForm.services.length === 0 ? (
                      <p className="text-sm text-amber-700">Aucun service ou pack sélectionné</p>
                    ) : (
                      <>
                        <div className="space-y-1 mb-3">
                          {projectForm.services.map((service, index) => (
                            <div key={index} className="flex items-center justify-between text-sm">
                              <span className="text-amber-800">
                                • {service.description} ({service.type === 'service' ? 'Service' : 'Pack'})
                              </span>
                              <span className="font-medium text-amber-900">{service.unit_price} FCFA</span>
                            </div>
                          ))}
                        </div>
                        <div className="pt-2 border-t border-amber-200">
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-amber-800">Total du projet:</span>
                            <span className="text-lg font-bold text-amber-900">
                              {projectForm.services.reduce((total, service) => total + service.unit_price, 0)} FCFA
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 py-3 px-4 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleCreateProject}
                    disabled={!projectForm.project_name.trim() || !projectForm.client_name.trim() || !projectForm.client_email.trim()}
                    className="flex-1 py-3 px-4 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Créer le projet
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Project Detail Modal */}
      <AnimatePresence>
        {showProjectDetail && (selectedProject || selectedSubmittedProject) && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-60"
              onClick={() => setShowProjectDetail(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: '100%' }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: '100%' }}
              className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl z-70 max-h-[90vh] overflow-y-auto"
            >
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedProject ? `Projet #${selectedProject.project_id}` : selectedSubmittedProject?.title}
                  </h3>
                  <button
                    onClick={() => setShowProjectDetail(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <XCircle className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>

              <div className="p-4 space-y-4">
                {selectedProject && (
                  <>
                    {/* Project Info */}
                    <div className="bg-gray-50 rounded-xl p-4">
                      <h4 className="font-medium text-gray-900 mb-3">Informations du projet</h4>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">Client:</span>
                          <span className="font-medium text-gray-900">{selectedProject.client_name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">Email:</span>
                          <span className="font-medium text-gray-900">{selectedProject.client_email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">Créé le:</span>
                          <span className="font-medium text-gray-900">{formatDate(selectedProject.created_at)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <DollarSign className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">Montant:</span>
                          <span className="font-medium text-gray-900">{selectedProject.total_amount} FCFA</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${getStatusColor(selectedProject.status)}`}>
                            {getStatusIcon(selectedProject.status)}
                            {selectedProject.status}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Services */}
                    {selectedProject.services && selectedProject.services.length > 0 && (
                      <div className="bg-gray-50 rounded-xl p-4">
                        <h4 className="font-medium text-gray-900 mb-3">Services ({selectedProject.services.length})</h4>
                        <div className="space-y-3">
                          {selectedProject.services.map((service, index) => (
                            <div key={index} className="bg-white rounded-lg p-3 border border-gray-200">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <Package className="w-4 h-4 text-amber-600" />
                                  <span className="text-sm font-medium text-gray-900">{service.service_name || service.pack_name}</span>
                                </div>
                                <span className="text-sm text-gray-600">{service.total_price} FCFA</span>
                              </div>
                              
                              {/* Steps for this service */}
                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-medium text-gray-700">Étapes ({service.conception_steps?.length || 0})</span>
                                  <button
                                    onClick={() => handleAddStepToService(service.id)}
                                    className="flex items-center gap-1 px-2 py-1 bg-amber-600 text-white rounded text-xs hover:bg-amber-700 transition-colors"
                                  >
                                    <Plus className="w-3 h-3" />
                                    Ajouter
                                  </button>
                                </div>
                                
                                {service.conception_steps && service.conception_steps.length > 0 ? (
                                  <div className="space-y-1">
                                    {service.conception_steps.map((step, stepIndex) => (
                                      <div key={stepIndex} className="bg-gray-50 rounded p-2">
                                        <div className="flex items-center justify-between mb-1">
                                          <span className="text-xs text-gray-700 font-medium">{step.name}</span>
                                          <button
                                            onClick={() => handleChangeStepStatus(step)}
                                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs border transition-colors hover:opacity-80 ${getStatusColor(step.status)}`}
                                          >
                                            {getStatusIcon(step.status)}
                                            {step.status_display}
                                          </button>
                                        </div>
                                        {step.description && (
                                          <p className="text-xs text-gray-600 mt-1">{step.description}</p>
                                        )}
                                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                                          <span>Ordre: {step.order_index}</span>
                                          {step.estimated_duration && (
                                            <span>• Durée: {step.estimated_duration}h</span>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-xs text-gray-500 italic">Aucune étape créée</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  </>
                )}

                {selectedSubmittedProject && (
                  <>
                    {/* Submitted Project Info */}
                    <div className="bg-gray-50 rounded-xl p-4">
                      <h4 className="font-medium text-gray-900 mb-3">Détails du projet</h4>
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm text-gray-600">Description:</span>
                          <p className="text-sm text-gray-900 mt-1">{selectedSubmittedProject.description}</p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Message:</span>
                          <p className="text-sm text-gray-900 mt-1">{selectedSubmittedProject.message}</p>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">Client:</span>
                          <span className="font-medium text-gray-900">{selectedSubmittedProject.user_name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">Email:</span>
                          <span className="font-medium text-gray-900">{selectedSubmittedProject.user_email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">Soumis le:</span>
                          <span className="font-medium text-gray-900">{formatDate(selectedSubmittedProject.created_at)}</span>
                        </div>
                        {selectedSubmittedProject.files_count > 0 && (
                          <div className="flex items-center gap-2 text-sm">
                            <File className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-600">Fichiers:</span>
                            <span className="font-medium text-gray-900">{selectedSubmittedProject.files_count} fichier(s)</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-sm">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${getStatusColor(selectedSubmittedProject.status)}`}>
                            {getStatusIcon(selectedSubmittedProject.status)}
                            {selectedSubmittedProject.status_display}
                          </span>
                        </div>
                      </div>
                    </div>

                    {selectedSubmittedProject.admin_response && (
                      <div className="bg-blue-50 rounded-xl p-4">
                        <h4 className="font-medium text-blue-900 mb-2">Réponse administrateur</h4>
                        <p className="text-sm text-blue-800">{selectedSubmittedProject.admin_response}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Create Step Modal */}
      <AnimatePresence>
        {showCreateStep && selectedProject && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-70"
              onClick={() => setShowCreateStep(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: '100%' }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: '100%' }}
              className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl z-80 max-h-[80vh] overflow-y-auto"
            >
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Créer une étape</h3>
                    {selectedProject && selectedServiceId && (
                      <p className="text-sm text-gray-600 mt-1">
                        Service: {selectedProject.services?.find(s => s.id === selectedServiceId)?.service_name || selectedProject.services?.find(s => s.id === selectedServiceId)?.pack_name}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setShowCreateStep(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <XCircle className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>

              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom de l'étape *
                  </label>
                  <input
                    type="text"
                    value={newStepName}
                    onChange={(e) => setNewStepName(e.target.value)}
                    placeholder="Ex: Conception de la maquette"
                    className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (optionnel)
                  </label>
                  <textarea
                    value={newStepDescription}
                    onChange={(e) => setNewStepDescription(e.target.value)}
                    placeholder="Description détaillée de l'étape..."
                    rows={3}
                    className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowCreateStep(false)}
                    className="flex-1 py-3 px-4 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleCreateStep}
                    disabled={!newStepName.trim()}
                    className="flex-1 py-3 px-4 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Créer l'étape
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Change Status Modal */}
      <AnimatePresence>
        {showStatusModal && selectedStep && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-70"
              onClick={() => setShowStatusModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: '100%' }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: '100%' }}
              className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl z-80 max-h-[70vh] overflow-y-auto"
            >
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Changer le statut</h3>
                    <p className="text-sm text-gray-600 mt-1">Étape: {selectedStep.name}</p>
                  </div>
                  <button
                    onClick={() => setShowStatusModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <XCircle className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>

              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Nouveau statut
                  </label>
                  <div className="space-y-2">
                    {[
                      { value: 'pending', label: 'En attente', icon: Clock },
                      { value: 'in_progress', label: 'En cours', icon: Play },
                      { value: 'completed', label: 'Terminé', icon: CheckCircle }
                    ].map(({ value, label, icon: Icon }) => (
                      <button
                        key={value}
                        onClick={() => setNewStatus(value as any)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-colors ${
                          newStatus === value
                            ? value === 'pending' 
                              ? 'border-yellow-500 bg-yellow-50'
                              : value === 'in_progress'
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Icon className={`w-5 h-5 ${
                          newStatus === value 
                            ? value === 'pending'
                              ? 'text-yellow-600'
                              : value === 'in_progress'
                              ? 'text-blue-600'
                              : 'text-green-600'
                            : 'text-gray-400'
                        }`} />
                        <div className="text-left">
                          <p className={`font-medium ${
                            newStatus === value 
                              ? value === 'pending'
                                ? 'text-yellow-900'
                                : value === 'in_progress'
                                ? 'text-blue-900'
                                : 'text-green-900'
                              : 'text-gray-900'
                          }`}>
                            {label}
                          </p>
                          <p className="text-xs text-gray-500">
                            {value === 'pending' && 'L\'étape n\'a pas encore commencé'}
                            {value === 'in_progress' && 'L\'étape est en cours de réalisation'}
                            {value === 'completed' && 'L\'étape est terminée'}
                          </p>
                        </div>
                        {newStatus === value && (
                          <CheckCircle className={`w-5 h-5 ml-auto ${
                            value === 'pending'
                              ? 'text-yellow-600'
                              : value === 'in_progress'
                              ? 'text-blue-600'
                              : 'text-green-600'
                          }`} />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowStatusModal(false)}
                    className="flex-1 py-3 px-4 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleUpdateStepStatus}
                    className="flex-1 py-3 px-4 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-colors"
                  >
                    Mettre à jour
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MobileGestionProjets;
